import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyHeader from "@/pages/property/PropertyHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SatisfactionWidget } from "@/components/SatisfactionWidget";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, FileText, UploadSimple, Download, Trash, FolderPlus,
  Folder, FolderOpen, CaretRight, PencilSimple, House, DotsThree,
  Eye, FilePdf, FileImage, FileDoc, FileZip, X, Robot,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { DemoBanner, HelpTip } from "@/components/DemoHelpers";
import { useAuth } from "@/lib/auth";
import { FilePreviewModal } from "@/components/FilePreviewModal";
import AiCopilotSection from "@/components/AiCopilotSection";
import { useNotifications } from "@/lib/notifications";
import NewPropertyForm from "@/components/NewPropertyForm";

const MAX_SIZE = 100 * 1024 * 1024; // 100 MB
const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;

const IMAGE_EXTS = ["png", "jpg", "jpeg", "webp", "gif", "svg", "bmp"];
const isImage = (name = "") => IMAGE_EXTS.includes((name.split(".").pop() || "").toLowerCase());
const isPdf = (name = "") => (name.split(".").pop() || "").toLowerCase() === "pdf";
const getExt = (name = "") => (name.split(".").pop() || "").toLowerCase();

const DocIcon = ({ filename, size = 22 }) => {
  const ext = getExt(filename);
  if (isImage(filename)) return <FileImage size={size} color="#0891b2" weight="duotone" />;
  if (isPdf(filename)) return <FilePdf size={size} color="#dc2626" weight="duotone" />;
  if (["doc", "docx", "odt"].includes(ext)) return <FileDoc size={size} color="#2563eb" weight="duotone" />;
  if (["zip", "rar", "7z"].includes(ext)) return <FileZip size={size} color="#a16207" weight="duotone" />;
  return <FileText size={size} color="#4F46E5" weight="duotone" />;
};

const fmtSize = (b) => {
  if (!b && b !== 0) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const emptyEdit = () => ({
  owner_type: "privato",
  address: "", city: "", province: "", zip_code: "", notes: "",
  owner_first_name: "", owner_last_name: "", owner_phone: "", owner_email: "",
  owner_vat_number: "", owner_company_name: "", owner_company_fiscal_code: "",
  owner_company_address: "",
  property_type: "residenziale", surface_sqm: 0, rooms: 0, year_built: "",
  energy_class: "", cadastral_data: "",
});

export default function PropertyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [currentFolderId, setCurrentFolderId] = useState(null); // null = root
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  // Iter207.18 — Pratiche relative a questo immobile, usate per legare la
  // cartella al suo servizio e mostrare il SatisfactionWidget quando la
  // pratica è `completed`.
  const [propertyPractices, setPropertyPractices] = useState([]);
  // Iter93/Iter97: pallini rossi per notifiche non lette immobile/cartella.
  // NON marchiamo l'immobile intero al mount: le notifiche si azzerano SOLO
  // quando l'utente entra nella specifica cartella (destinazione finale).
  const { propertySummary, markRead } = useNotifications();
  const unreadForProperty = (propertySummary?.per_property || {})[id] || 0;
  const perFolder = propertySummary?.per_folder || {};

  // Auto-mark al cambio cartella: appena l'utente entra in una cartella
  // marca come lette tutte le notifiche `folder_id=<currentFolderId>`.
  useEffect(() => {
    if (!currentFolderId) return;
    if ((perFolder[currentFolderId] || 0) === 0) return;
    markRead({ folder_id: currentFolderId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFolderId]);

  // Folder dialogs
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameFolder, setRenameFolder] = useState(null); // {id, name}
  const [deleteFolder, setDeleteFolder] = useState(null); // {id, name}
  const [deleteDoc, setDeleteDoc] = useState(null); // {id, name}

  // Property edit / delete
  const [editOpen, setEditOpen] = useState(false);
  const [editDetailsOpen, setEditDetailsOpen] = useState(false);
  useEffect(() => { if (editOpen) setEditDetailsOpen(false); }, [editOpen]);
  const [editForm, setEditForm] = useState(emptyEdit());
  const [deletePropOpen, setDeletePropOpen] = useState(false);
  // Iter209y — Variante del dialog di eliminazione:
  //   "generic"    → nessun servizio, conferma leggera.
  //   "completed"  → solo servizi completati, avvisa sui documenti.
  //   "blocked"    → almeno un servizio aperto: sola visualizzazione.
  const [deleteVariant, setDeleteVariant] = useState("generic");
  const [deleteChecking, setDeleteChecking] = useState(false);

  const load = () => api.get(`/properties/${id}`).then(r => setData(r.data));
  useEffect(() => { load(); }, [id]);

  // Iter209e — Reload pratiche (usato dopo "Ho integrato i documenti").
  const reloadPractices = () => {
    api.get("/practices", { params: { scope: "all" } })
      .then((r) => {
        const list = (r.data || []).filter((pr) => pr.property_id === id);
        setPropertyPractices(list);
      })
      .catch(() => {});
  };

  // Iter209e — Richieste di integrazione ancora aperte sulle pratiche di
  // questo immobile (pending o received). Flatten in un unico array così
  // il banner può essere mostrato per ognuna in sequenza.
  const pendingIntegrations = useMemo(() => {
    const out = [];
    for (const pr of propertyPractices) {
      const reqs = pr.integration_requests || [];
      for (const r of reqs) {
        if (r.status === "pending" || r.status === "received") {
          out.push({
            practice_id: pr._id,
            service_name: pr.service_name,
            request_id: r.id,
            message: r.message,
            requested_by_name: r.requested_by_name,
            requested_at: r.requested_at,
          });
        }
      }
    }
    return out;
  }, [propertyPractices]);

  const [integrationSubmitting, setIntegrationSubmitting] = useState(null);
  // Iter209f — Nota opzionale per l'operatore, per singola richiesta.
  const [integrationNotes, setIntegrationNotes] = useState({});
  const handleIntegrationComplete = async (practiceId, requestId) => {
    if (integrationSubmitting) return;
    setIntegrationSubmitting(requestId);
    const note = (integrationNotes[requestId] || "").trim();
    try {
      await api.post(`/practices/${practiceId}/integration-complete`, {
        request_id: requestId,
        ...(note ? { note } : {}),
      });
      toast.success("Grazie! Housetag è stata informata.");
      setIntegrationNotes((s) => { const n = { ...s }; delete n[requestId]; return n; });
      reloadPractices();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossibile completare l'operazione");
    } finally {
      setIntegrationSubmitting(null);
    }
  };

  // Iter207.18 — Fetch pratiche di questo immobile per legare le cartelle al
  // servizio e mostrare il SatisfactionWidget su cartelle di pratiche completate.
  useEffect(() => {
    api.get("/practices", { params: { scope: "all" } })
      .then((r) => {
        const list = (r.data || []).filter((pr) => pr.property_id === id);
        setPropertyPractices(list);
      })
      .catch(() => setPropertyPractices([]));
  }, [id]);

  // Iter207.18 — Se arriviamo da PracticeQuickView con `?folder_name=<service>`,
  // dopo il caricamento delle cartelle apriamo direttamente quella matching.
  // Eseguito una sola volta: i reload successivi di `data` non devono
  // riportare l'utente alla cartella del query param.
  const folderParamAppliedRef = useRef(false);
  useEffect(() => {
    const target = searchParams.get("folder_name");
    if (folderParamAppliedRef.current || !target || !data?.folders) return;
    folderParamAppliedRef.current = true;
    const match = data.folders.find(
      (f) => (f.parent_folder_id || null) === null && f.name === target,
    );
    if (match && currentFolderId !== match._id) {
      setCurrentFolderId(match._id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.folders, searchParams]);

  const p = data?.property;
  const allFolders = data?.folders || [];
  const allDocs = data?.documents || [];

  const foldersHere = useMemo(
    () => allFolders.filter(f => (f.parent_folder_id || null) === currentFolderId)
                    .sort((a, b) => a.name.localeCompare(b.name)),
    [allFolders, currentFolderId]
  );
  const docsHere = useMemo(
    () => allDocs.filter(d => (d.folder_id || null) === currentFolderId),
    [allDocs, currentFolderId]
  );

  // Breadcrumb chain from root -> current
  const breadcrumb = useMemo(() => {
    const chain = [];
    let cur = currentFolderId;
    const byId = new Map(allFolders.map(f => [f._id, f]));
    while (cur) {
      const f = byId.get(cur);
      if (!f) break;
      chain.unshift(f);
      cur = f.parent_folder_id || null;
    }
    return chain;
  }, [allFolders, currentFolderId]);

  const openEdit = () => {
    if (!p) return;
    setEditForm({
      owner_type: p.owner_type || "privato",
      address: p.address || "",
      city: p.city || "",
      province: p.province || "",
      zip_code: p.zip_code || "",
      notes: p.notes || "",
      owner_first_name: p.owner_first_name || "",
      owner_last_name: p.owner_last_name || "",
      owner_phone: p.owner_phone || "",
      owner_email: p.owner_email || "",
      owner_vat_number: p.owner_vat_number || "",
      owner_company_name: p.owner_company_name || "",
      owner_company_fiscal_code: p.owner_company_fiscal_code || "",
      owner_company_address: p.owner_company_address || "",
      property_type: p.property_type || "residenziale",
      surface_sqm: p.surface_sqm || 0,
      rooms: p.rooms || 0,
      year_built: p.year_built || "",
      energy_class: p.energy_class || "",
      cadastral_data: p.cadastral_data || "",
    });
    setEditOpen(true);
  };

  const patchEditForm = (patch) => setEditForm((f) => ({ ...f, ...patch }));

  const saveEdit = async () => {
    try {
      const ownerLabel = editForm.owner_type === "azienda"
        ? editForm.owner_company_name
        : `${editForm.owner_first_name} ${editForm.owner_last_name}`.trim();
      const payload = {
        ...editForm,
        surface_sqm: Number(editForm.surface_sqm) || 0,
        rooms: Number(editForm.rooms) || 0,
        year_built: editForm.year_built ? Number(editForm.year_built) : null,
        title: `${editForm.address}${editForm.city ? ", " + editForm.city : ""}${ownerLabel ? " — " + ownerLabel : ""}`,
      };
      await api.patch(`/properties/${id}`, payload);
      toast.success("Immobile aggiornato");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const confirmDeleteProperty = async () => {
    try {
      await api.delete(`/properties/${id}`);
      toast.success("Immobile eliminato");
      navigate("/properties");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore eliminazione");
    }
  };

  // Iter209y — Prima di aprire il dialog, chiediamo al backend lo stato
  // dei servizi legati all'immobile. Il testo del dialog e la
  // possibilità di eliminare dipendono da questa risposta.
  const openDeleteDialog = async () => {
    setDeleteChecking(true);
    try {
      const r = await api.get(`/properties/${id}/services-status`);
      const s = r.data || {};
      if (s.has_open) setDeleteVariant("blocked");
      else if (s.has_completed) setDeleteVariant("completed");
      else setDeleteVariant("generic");
      setDeletePropOpen(true);
    } catch (_e) {
      // Se la fetch fallisce fallback a conferma generica.
      setDeleteVariant("generic");
      setDeletePropOpen(true);
    } finally {
      setDeleteChecking(false);
    }
  };

  const [previewDoc, setPreviewDoc] = useState(null); // {file_url, filename}
  const [dragActive, setDragActive] = useState(false);

  const uploadFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setUploading(true);
    let ok = 0;
    const valid = files.filter((f) => {
      if (f.size <= MAX_SIZE) return true;
      toast.error(`${f.name}: file troppo grande (max 100 MB)`);
      return false;
    });
    // Iter209ew — Upload in parallelo (max 3 alla volta); ogni documento
    // compare subito in lista dalla risposta, ricarica unica alla fine.
    const uploadOne = async (f) => {
      const fd = new FormData();
      fd.append("file", f);
      if (currentFolderId) fd.append("folder_id", currentFolderId);
      try {
        const r = await api.post(`/properties/${id}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 10 * 60 * 1000,
        });
        ok++;
        if (r?.data) setData((d) => (d ? { ...d, documents: [...(d.documents || []), r.data] } : d));
      } catch (err) {
        toast.error(`${f.name}: ${err?.response?.data?.detail || "errore upload"}`);
      }
    };
    const queue = [...valid];
    await Promise.all(Array.from({ length: Math.min(3, queue.length) }, async () => {
      while (queue.length) await uploadOne(queue.shift());
    }));
    setUploading(false);
    if (ok) toast.success(ok === 1 ? "Documento caricato" : `${ok} file caricati`);
    await load();
  };

  const upload = async (e) => {
    await uploadFiles(e.target.files);
    e.target.value = "";
  };

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer?.files?.length) uploadFiles(e.dataTransfer.files);
  };

  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name) { toast.error("Nome cartella obbligatorio"); return; }
    try {
      await api.post(`/properties/${id}/folders`, { name, parent_folder_id: currentFolderId });
      toast.success("Cartella creata");
      setNewFolderOpen(false);
      setNewFolderName("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore creazione cartella");
    }
  };

  const submitRename = async () => {
    if (!renameFolder) return;
    const name = renameFolder.name.trim();
    if (!name) { toast.error("Nome cartella obbligatorio"); return; }
    try {
      await api.patch(`/properties/${id}/folders/${renameFolder.id}`, { name });
      toast.success("Cartella rinominata");
      setRenameFolder(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const confirmDeleteFolder = async () => {
    if (!deleteFolder) return;
    try {
      await api.delete(`/properties/${id}/folders/${deleteFolder.id}`);
      toast.success("Cartella eliminata");
      setDeleteFolder(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const confirmDeleteDoc = async () => {
    if (!deleteDoc) return;
    try {
      await api.delete(`/properties/${id}/documents/${deleteDoc.id}`);
      toast.success("Documento eliminato");
      setDeleteDoc(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    }
  };

  const archiveOrReactivate = async () => {
    if (p.is_archived) {
      if (!window.confirm("Riattivare questo immobile?")) return;
      await api.post(`/properties/${id}/reactivate`);
      toast.success("Immobile riattivato");
    } else {
      if (!window.confirm("Archiviare questo immobile? Rimarrà accessibile in sola lettura nella tab Archiviati.")) return;
      await api.post(`/properties/${id}/archive`);
      toast.success("Immobile archiviato");
    }
    load();
  };

  if (!data) return <div>Caricamento...</div>;

  return (
    <div>
      <PropertyHeader
        property={p}
        allDocsCount={allDocs.length}
        onEdit={openEdit}
        onArchive={archiveOrReactivate}
        onDelete={openDeleteDialog}
      />

      {/* Archivio Documenti con cartelle + drag&drop */}
      <div
        className={`kpi-card relative transition-all ${dragActive ? "ring-2 ring-purple-500 bg-purple-50/40" : ""}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid="doc-archive-dropzone"
      >
        {/* Iter209e — Banner "Ho integrato i documenti" per ogni richiesta
            di integrazione ancora pending/received sulle pratiche di
            questo immobile. */}
        {pendingIntegrations.length > 0 && (
          <div className="mb-4 space-y-3" data-testid="integration-banners">
            {pendingIntegrations.map((it) => (
              <div
                key={it.request_id}
                className="rounded-xl border border-amber-200 bg-amber-50 p-4"
                data-testid={`integration-banner-${it.request_id}`}
              >
                <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold">
                  Integrazione documenti richiesta
                </div>
                <div
                  className="mt-2 text-sm text-slate-900 whitespace-pre-line"
                  data-testid={`integration-message-${it.request_id}`}
                >
                  {it.message || "—"}
                </div>
                <div className="mt-3 text-xs text-slate-500">
                  Richiesta da <strong>{it.requested_by_name || "Housetag"}</strong>
                  {it.requested_at ? (
                    <>
                      {" · "}
                      {new Date(it.requested_at).toLocaleString("it-IT", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </>
                  ) : null}
                  {it.service_name && (
                    <>{" · "}<span className="text-amber-800">{it.service_name}</span></>
                  )}
                </div>

                {/* Iter209f — Nota opzionale per l'operatore */}
                <div className="mt-3">
                  <label
                    htmlFor={`integration-note-${it.request_id}`}
                    className="text-[11px] font-semibold text-slate-600 block mb-1"
                  >
                    Aggiungi una nota per l'operatore (opzionale)
                  </label>
                  <textarea
                    id={`integration-note-${it.request_id}`}
                    data-testid={`integration-note-${it.request_id}`}
                    rows={2}
                    maxLength={800}
                    placeholder="Es. Ho caricato la nuova visura e la planimetria vidimata nella cartella APE."
                    value={integrationNotes[it.request_id] || ""}
                    onChange={(e) =>
                      setIntegrationNotes((s) => ({ ...s, [it.request_id]: e.target.value }))
                    }
                    className="w-full text-sm rounded-lg border border-amber-200 bg-white/70 focus:border-amber-400 focus:ring-2 focus:ring-amber-200 focus:outline-none p-2 resize-y"
                    disabled={integrationSubmitting === it.request_id}
                  />
                  <div className="text-[10px] text-slate-400 text-right mt-1">
                    {(integrationNotes[it.request_id] || "").length}/800
                  </div>
                </div>

                <div className="mt-2 flex justify-end">
                  <Button
                    onClick={() => handleIntegrationComplete(it.practice_id, it.request_id)}
                    disabled={integrationSubmitting === it.request_id}
                    className="ht-gradient border-0 text-white"
                    data-testid={`integration-complete-btn-${it.request_id}`}
                  >
                    {integrationSubmitting === it.request_id ? "Invio…" : "Ho integrato i documenti"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {dragActive && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-purple-100/85 rounded-lg border-2 border-dashed border-purple-500 pointer-events-none">
            <div className="text-center">
              <UploadSimple size={44} weight="duotone" className="mx-auto text-purple-700" />
              <div className="mt-2 font-display text-lg font-bold text-purple-900">Rilascia qui per caricare</div>
              <div className="text-xs text-purple-800">Max 100 MB per file · {currentFolderId ? "verrà salvato nella cartella corrente" : "verrà salvato nella radice"}</div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h3 className="font-display text-xl font-semibold flex items-center gap-2">
            Archivio Documenti
            {unreadForProperty > 0 && (
              <span
                className="min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center shadow-lg shadow-red-500/40"
                data-testid={`archive-unread-badge-${id}`}
                title={`${unreadForProperty} documenti appena consegnati — apri la cartella per rimuovere il pallino`}
              >
                {unreadForProperty > 99 ? "99+" : unreadForProperty}
              </span>
            )}
            <HelpTip text="Trascina uno o più file qui, oppure clicca Carica. Massimo 100 MB per file." />
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            <Button variant="outline" onClick={() => { setNewFolderName(""); setNewFolderOpen(true); }} data-testid="new-folder-btn">
              <FolderPlus size={16} className="mr-2" weight="bold" /> Nuova cartella
            </Button>
            <input ref={fileRef} type="file" hidden multiple onChange={upload} data-testid="doc-file-input" />
            <Button onClick={() => fileRef.current?.click()} className="ht-gradient border-0" disabled={uploading} data-testid="upload-doc-btn">
              <UploadSimple size={16} className="mr-2" weight="bold" /> {uploading ? "Caricamento…" : "Carica file"}
            </Button>
          </div>
        </div>

        {/* Breadcrumb — Iter207.15:
            • Radice (nessuna cartella aperta): mostra solo l'icona House.
            • Dentro una cartella: mostra freccia ← cliccabile per salire
              di un livello (parent). */}
        <div className="flex items-center flex-wrap gap-1 text-sm mb-4 pb-3 border-b">
          {breadcrumb.length === 0 ? (
            <button
              onClick={() => setCurrentFolderId(null)}
              className="inline-flex items-center justify-center w-9 h-9 rounded hover:bg-purple-50 text-muted-foreground hover:text-purple-700"
              data-testid="breadcrumb-root"
              aria-label="Radice archivio"
              title="Radice archivio"
            >
              <House size={18} weight="duotone" />
            </button>
          ) : (
            <button
              onClick={() => {
                // Sali di un livello: se siamo dentro una sub-cartella, torna
                // al parent; altrimenti alla radice.
                const parent = breadcrumb.length > 1 ? breadcrumb[breadcrumb.length - 2]._id : null;
                setCurrentFolderId(parent);
              }}
              className="inline-flex items-center justify-center w-9 h-9 rounded hover:bg-purple-50 text-muted-foreground hover:text-purple-700"
              data-testid="breadcrumb-back"
              aria-label="Torna alla cartella superiore"
              title="Torna alla cartella superiore"
            >
              <ArrowLeft size={18} weight="bold" />
            </button>
          )}
          {breadcrumb.map((f, idx) => (
            <React.Fragment key={f._id}>
              <CaretRight size={12} className="text-muted-foreground" />
              <button onClick={() => setCurrentFolderId(f._id)}
                className={`px-2 py-1 rounded hover:bg-purple-50 ${idx === breadcrumb.length - 1 ? "font-semibold text-purple-700" : "text-muted-foreground"}`}
                data-testid={`breadcrumb-${f._id}`}>
                {f.name}
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Iter207.18 — SatisfactionWidget legato alla cartella di una pratica
            completata. Match per nome cartella root (== service_name). Se la
            pratica non è completed o non esiste, non appare nulla. */}
        {(() => {
          if (breadcrumb.length === 0) return null;
          const rootFolderName = breadcrumb[0].name;
          const practice = propertyPractices.find(
            (pr) => pr.service_name === rootFolderName && pr.status === "completed",
          );
          if (!practice) return null;
          if (practice.satisfaction_score != null) {
            return (
              <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="folder-satisfaction-done">
                <strong className="text-emerald-800">✓ Feedback inviato:</strong>{" "}
                <span className="text-emerald-700">
                  {practice.satisfaction_score >= 100 ? "😄 Soddisfatto"
                    : practice.satisfaction_score >= 50 ? "😐 Neutro" : "😞 Insoddisfatto"}
                </span>
              </div>
            );
          }
          return (
            <div className="mb-4" data-testid="folder-satisfaction-widget">
              <SatisfactionWidget
                practiceId={practice._id}
                existingScore={null}
                onRated={() => {
                  api.get("/practices", { params: { scope: "all" } })
                    .then((r) => {
                      const list = (r.data || []).filter((pr) => pr.property_id === id);
                      setPropertyPractices(list);
                    })
                    .catch(() => {});
                }}
              />
            </div>
          );
        })()}

        {/* Empty state */}
        {foldersHere.length === 0 && docsHere.length === 0 ? (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className={`w-full text-center py-12 px-4 rounded-xl border-2 border-dashed transition-colors ${dragActive ? "border-purple-500 bg-purple-50/60" : "border-purple-200 hover:border-purple-400 hover:bg-purple-50/40"}`}
            data-testid="doc-empty-state"
          >
            <p className="text-base font-semibold text-slate-800">📂 Carica qui i tuoi documenti.</p>
            <p className="text-sm text-slate-600 mt-2">L'AI controllerà le scadenze importanti e ti avviserà 15 giorni prima.</p>
            <p className="text-xs font-bold tracking-wide text-purple-700 mt-3">🔒 I DOCUMENTI PUOI VEDERLI SOLO TU E IL TUO TEAM.</p>
          </button>
        ) : (
          <div className="space-y-2">
            {/* Folders first */}
            {foldersHere.map(f => {
              const folderUnread = perFolder[f._id] || 0;
              return (
              <div key={f._id}
                className={`group flex items-center justify-between p-3 border rounded-md hover:bg-[color:hsl(var(--surface-alt))] transition-colors ${folderUnread > 0 ? "border-red-200 bg-red-50/30" : ""}`}
                data-testid={`folder-row-${f._id}`}>
                <button onClick={() => setCurrentFolderId(f._id)} className="flex items-center gap-3 flex-1 text-left min-w-0">
                  <div className="relative shrink-0">
                    <FolderOpen size={22} color="#7c3aed" weight="duotone" />
                    {folderUnread > 0 && (
                      <span
                        className="absolute -top-1.5 -right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shadow ring-1 ring-white"
                        data-testid={`folder-badge-${f._id}`}
                        title={`${folderUnread} documenti appena consegnati`}
                      >
                        {folderUnread > 9 ? "9+" : folderUnread}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{f.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {allFolders.filter(x => x.parent_folder_id === f._id).length} sottocartelle · {allDocs.filter(d => d.folder_id === f._id).length} file
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100">
                  <Button variant="ghost" size="sm" onClick={() => setRenameFolder({ id: f._id, name: f.name })}
                    data-testid={`rename-folder-${f._id}`}>
                    <PencilSimple size={14} />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setDeleteFolder({ id: f._id, name: f.name })}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`delete-folder-${f._id}`}>
                    <Trash size={14} />
                  </Button>
                </div>
              </div>
              );
            })}
            {/* Documents */}
            {docsHere.map(d => {
              const canPreview = isImage(d.filename) || isPdf(d.filename);
              const isImg = isImage(d.filename);
              const fileHref = `${API_ORIGIN}${d.signed_url || d.file_url}`;
              return (
                <div key={d._id}
                  className="group flex items-center justify-between p-3 border rounded-md hover:bg-[color:hsl(var(--surface-alt))] transition-colors"
                  data-testid={`doc-row-${d._id}`}>
                  <button
                    className="flex items-center gap-3 min-w-0 flex-1 text-left"
                    onClick={() => canPreview && setPreviewDoc(d)}
                    title={canPreview ? "Clicca per anteprima" : d.filename}
                    data-testid={`doc-preview-btn-${d._id}`}
                  >
                    {isImg ? (
                      <img
                        src={fileHref}
                        alt={d.filename}
                        className="w-11 h-11 rounded object-cover border flex-shrink-0"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-11 h-11 rounded bg-slate-50 border flex items-center justify-center flex-shrink-0">
                        <DocIcon filename={d.filename} size={22} />
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{d.filename}</div>
                      <div className="text-xs text-muted-foreground flex items-center gap-2">
                        <span>{fmtSize(d.file_size)}</span>
                        {canPreview && <span className="text-purple-600 font-medium">· Anteprima disponibile</span>}
                      </div>
                    </div>
                  </button>
                  <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100">
                    {canPreview && (
                      <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(d)} data-testid={`preview-doc-${d._id}`} title="Anteprima">
                        <Eye size={16} />
                      </Button>
                    )}
                    <a href={fileHref} target="_blank" rel="noopener noreferrer" title="Scarica">
                      <Button variant="ghost" size="sm" data-testid={`download-doc-${d._id}`}><Download size={16} /></Button>
                    </a>
                    <Button variant="ghost" size="sm" onClick={() => setDeleteDoc({ id: d._id, name: d.filename })}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`delete-doc-${d._id}`}>
                      <Trash size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Iter208.49 — La sezione "Appuntamenti Collettivi" è stata rimossa
          da PropertyDetail: la gestione appuntamenti è centralizzata nella
          pagina globale /calendar (ProCalendarPage) per il PRO. */}

      {/* Iter178 — Agente AI inline (sostituisce il vecchio widget fluttuante). */}
      <AiCopilotSection propertyId={id} onDocumentsUploaded={load} />
      <div className="block sm:hidden mt-6 mb-20">
        <Button
          variant="outline"
          onClick={openDeleteDialog}
          className="w-full border-red-300 text-red-700 hover:bg-red-50"
          data-testid="delete-property-btn-mobile"
        >
          <Trash size={16} className="mr-2" /> Elimina
        </Button>
      </div>

      {/* Dialog: Nuova cartella */}
      <Dialog open={newFolderOpen} onOpenChange={setNewFolderOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Nuova cartella</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Nome cartella</Label>
            <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)}
              placeholder="es. Planimetrie 2024" autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") createFolder(); }}
              data-testid="new-folder-name-input" />
            {breadcrumb.length > 0 && (
              <p className="text-xs text-muted-foreground">
                Verrà creata dentro: <strong>{breadcrumb.map(b => b.name).join(" / ")}</strong>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNewFolderOpen(false)}>Annulla</Button>
            <Button onClick={createFolder} className="ht-gradient border-0" data-testid="new-folder-confirm-btn">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Rinomina cartella */}
      <Dialog open={!!renameFolder} onOpenChange={(v) => !v && setRenameFolder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Rinomina cartella</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Nuovo nome</Label>
            <Input value={renameFolder?.name || ""}
              onChange={e => setRenameFolder({ ...renameFolder, name: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter") submitRename(); }}
              autoFocus data-testid="rename-folder-input" />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameFolder(null)}>Annulla</Button>
            <Button onClick={submitRename} className="ht-gradient border-0" data-testid="rename-folder-confirm-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Elimina cartella */}
      <Dialog open={!!deleteFolder} onOpenChange={(v) => !v && setDeleteFolder(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Eliminare la cartella?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Stai per eliminare <strong>{deleteFolder?.name}</strong> e tutto il suo contenuto (sottocartelle e file). L'operazione è definitiva.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteFolder(null)}>Annulla</Button>
            <Button onClick={confirmDeleteFolder} className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-folder-confirm-btn">
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Elimina documento */}
      <Dialog open={!!deleteDoc} onOpenChange={(v) => !v && setDeleteDoc(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Eliminare il documento?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Stai per eliminare <strong>{deleteDoc?.name}</strong>. L'operazione è definitiva.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteDoc(null)}>Annulla</Button>
            <Button onClick={confirmDeleteDoc} className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-doc-confirm-btn">
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Anteprima documento — usa il componente condiviso FilePreviewModal
          per ereditare tutti i fix (X singola funzionante, X-Frame per PDF, ecc.). */}
      <FilePreviewModal
        doc={previewDoc}
        onClose={() => setPreviewDoc(null)}
      />

      {/* Dialog: Modifica immobile — Iter209dz1 usa NewPropertyForm condiviso */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica immobile</DialogTitle></DialogHeader>
          <NewPropertyForm
            value={editForm}
            onChange={patchEditForm}
            onSubmit={saveEdit}
            submitLabel="Salva modifiche"
            testidPrefix="edit-prop"
            showNotes={true}
            hideOwnerFields={true}
            onCancel={() => setEditOpen(false)}
            cancelLabel="Annulla"
            extraFields={(
              <div className="border-t pt-3" data-testid="edit-prop-advanced">
                <button
                  type="button"
                  onClick={() => setEditDetailsOpen((v) => !v)}
                  className="flex items-center gap-1.5 text-sm font-semibold text-purple-700 hover:text-purple-900"
                  aria-expanded={editDetailsOpen}
                  data-testid="edit-prop-details-toggle"
                >
                  <CaretRight size={14} weight="bold" className={`transition-transform duration-200 ${editDetailsOpen ? "rotate-90" : ""}`} />
                  {editDetailsOpen ? "− Dettagli" : "+ Dettagli"}
                </button>
                {editDetailsOpen && (
                <div className="space-y-3 mt-3" data-testid="edit-prop-details-section">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Tipologia</Label>
                    <select
                      value={editForm.property_type}
                      onChange={(e) => patchEditForm({ property_type: e.target.value })}
                      className="h-10 w-full border rounded-md px-2 text-sm"
                      data-testid="edit-prop-type"
                    >
                      <option value="residenziale">Residenziale</option>
                      <option value="commerciale">Commerciale</option>
                      <option value="industriale">Industriale</option>
                      <option value="terreno">Terreno</option>
                    </select>
                  </div>
                  <div>
                    <Label>Superficie (mq)</Label>
                    <Input
                      type="number"
                      value={editForm.surface_sqm}
                      onChange={(e) => patchEditForm({ surface_sqm: e.target.value })}
                      data-testid="edit-prop-surface"
                    />
                  </div>
                  <div>
                    <Label>Vani</Label>
                    <Input
                      type="number"
                      value={editForm.rooms}
                      onChange={(e) => patchEditForm({ rooms: e.target.value })}
                      data-testid="edit-prop-rooms"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <Label>Anno costruzione</Label>
                    <Input
                      type="number"
                      value={editForm.year_built}
                      onChange={(e) => patchEditForm({ year_built: e.target.value })}
                      placeholder="es. 1998"
                      data-testid="edit-prop-year"
                    />
                  </div>
                  <div>
                    <Label>Classe energetica</Label>
                    <Input
                      value={editForm.energy_class}
                      onChange={(e) => patchEditForm({ energy_class: e.target.value })}
                      placeholder="es. A"
                      data-testid="edit-prop-energy"
                    />
                  </div>
                  <div>
                    <Label>Dati catastali</Label>
                    <Input
                      value={editForm.cadastral_data}
                      onChange={(e) => patchEditForm({ cadastral_data: e.target.value })}
                      placeholder="Fg. Part. Sub."
                      data-testid="edit-prop-cadastral"
                    />
                  </div>
                </div>
                </div>
                )}
              </div>
            )}
          />
        </DialogContent>
      </Dialog>

      {/* Dialog: Elimina immobile — Iter209y con 3 varianti */}
      <Dialog open={deletePropOpen} onOpenChange={setDeletePropOpen}>
        <DialogContent className="max-w-md" data-testid={`delete-property-dialog-${deleteVariant}`}>
          {deleteVariant === "blocked" && (
            <>
              <DialogHeader>
                <DialogTitle className="text-red-700">Impossibile eliminare</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-700" data-testid="delete-property-blocked-msg">
                Non puoi eliminare questo immobile perché ha servizi ancora attivi.
                Attendi il completamento di tutti i servizi prima di procedere.
              </p>
              <DialogFooter>
                <Button onClick={() => setDeletePropOpen(false)} data-testid="delete-property-blocked-close">
                  Ho capito
                </Button>
              </DialogFooter>
            </>
          )}
          {deleteVariant === "completed" && (
            <>
              <DialogHeader>
                <DialogTitle>Attenzione: documenti dei servizi</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-700" data-testid="delete-property-completed-msg">
                Questo immobile ha servizi completati con documenti prodotti da
                Housetag. Hai già scaricato tutti i documenti dei servizi
                acquistati? Una volta eliminato l'immobile non sarà più
                possibile recuperarli.
              </p>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setDeletePropOpen(false)}
                  data-testid="delete-property-completed-abort"
                >
                  Non ancora, torna indietro
                </Button>
                <Button
                  onClick={confirmDeleteProperty}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-property-completed-confirm"
                >
                  Sì, ho scaricato tutto - Elimina
                </Button>
              </DialogFooter>
            </>
          )}
          {deleteVariant === "generic" && (
            <>
              <DialogHeader>
                <DialogTitle>Eliminare l'immobile?</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-slate-700" data-testid="delete-property-generic-msg">
                Sei sicuro di voler eliminare questo immobile? L'operazione è irreversibile.
              </p>
              <DialogFooter>
                <Button
                  variant="ghost"
                  onClick={() => setDeletePropOpen(false)}
                  data-testid="delete-property-generic-abort"
                >
                  Annulla
                </Button>
                <Button
                  onClick={confirmDeleteProperty}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="delete-property-confirm-btn"
                >
                  Elimina
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Iter178 — Rimossi il bottone flottante "Agente AI" e AiCopilotPanel
          in favore di `AiCopilotSection` inline sotto Appuntamenti. */}
    </div>
  );
}
